import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  LocalShipping,
  AttachMoney,
  Scale,
  Schedule,
  EmojiObjects,
  ShowChart,
  DateRange
} from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';
import { styled } from '@mui/material/styles';
import moment from 'moment-timezone';

// ─── Styled ───────────────────────────────────────────────────────────────────

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  height: '100%'
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  borderTop: '3px solid',
  height: '100%'
}));

const TH = styled(TableCell)(() => ({
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#555',
  padding: '6px 10px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e0e0e0',
  whiteSpace: 'nowrap'
}));

const TD = styled(TableCell)(() => ({
  fontSize: '0.72rem',
  padding: '5px 10px',
  borderBottom: '1px solid #f3f3f3'
}));

const StyledToggle = styled(ToggleButton)(({ selectedcolor }) => ({
  padding: '5px 20px',
  fontSize: '0.8rem',
  fontWeight: 700,
  textTransform: 'none',
  border: '2px solid #e0e0e0',
  color: '#666',
  borderRadius: '8px !important',
  gap: 6,
  transition: 'all 0.2s',
  '&.Mui-selected': {
    backgroundColor: `${selectedcolor} !important`,
    color: '#fff !important',
    borderColor: `${selectedcolor} !important`,
    boxShadow: `0 2px 8px ${selectedcolor}55`
  },
  '&:hover': { backgroundColor: '#f5f5f5', borderColor: '#bbb' }
}));

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const MODE_COLORS = ['#1565C0', '#00897B', '#FFA000', '#8E24AA', '#546E7A'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtM = (val, isCurr = false) => {
  if (!val || isNaN(val)) return isCurr ? '$0' : '0';
  if (val >= 1_000_000)
    return (isCurr ? '$' : '') + (val / 1_000_000).toFixed(2) + 'M';
  if (val >= 1_000) return (isCurr ? '$' : '') + (val / 1_000).toFixed(1) + 'K';
  return (isCurr ? '$' : '') + Math.round(val).toLocaleString();
};

const pctDiff = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return (((curr - prev) / Math.abs(prev)) * 100).toFixed(1);
};

/**
 * Builds a short readable label from two moment dates.
 * If same year  → "Oct 28 – Jan 21, 2025"
 * If diff years → "Oct 28, 2024 – Jan 21, 2026"
 */
const buildRangeLabel = (start, end) => {
  if (start.year() === end.year()) {
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  }
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

/**
 * Short column header for tables/charts — shows year range like "2024–26"
 * or just "2025" if single year.
 */
const buildShortLabel = (start, end) => {
  if (start.year() === end.year()) return start.format('YYYY');
  return `${start.format('YYYY')}–${end.format('YY')}`;
};

// ─── Delta Badge ──────────────────────────────────────────────────────────────

const Delta = ({ curr, prev, lowerIsBetter = false, isPts = false }) => {
  const p = pctDiff(curr, prev);
  if (p === null)
    return (
      <Typography variant='caption' color='text.disabled'>
        —
      </Typography>
    );
  const val = parseFloat(p);
  const isUp = val > 0;
  const isGood = lowerIsBetter ? !isUp : isUp;
  const color = val === 0 ? '#888' : isGood ? '#2e7d32' : '#d32f2f';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
      {isUp ? (
        <TrendingUp sx={{ fontSize: 13, color }} />
      ) : (
        <TrendingDown sx={{ fontSize: 13, color }} />
      )}
      <Typography
        variant='caption'
        sx={{ color, fontWeight: 700, fontSize: '0.7rem' }}
      >
        {isUp ? '+' : ''}
        {isPts ? (curr - prev).toFixed(1) + ' pts' : p + '%'}
      </Typography>
    </Box>
  );
};

// ─── Compute all metrics from a shipments array ───────────────────────────────

const computeMetrics = ships => {
  const freight = ships.reduce((s, i) => s + (i.quoteNetCharge || 0), 0);
  const count = ships.length;
  const weight = ships.reduce((s, i) => s + (i.totalNetWeight || 0), 0);
  const costPerLb = weight > 0 ? freight / weight : 0;

  // OTD — LTL Outbound Delivered only
  const ltlDel = ships.filter(
    s =>
      s.shipmentStatus === 'Delivered' &&
      s.shipmentType === 'LTL' &&
      s.shipmentTypeRule === 'Outbound' &&
      s.actualDeliveryDate &&
      s.scheduledDeliveryDate
  );
  const onTime = ltlDel.filter(
    s => new Date(s.actualDeliveryDate) <= new Date(s.scheduledDeliveryDate)
  );
  const otdPct = ltlDel.length > 0 ? (onTime.length / ltlDel.length) * 100 : 0;

  // Monthly freight — keyed by calendar month name only (Jan, Feb…)
  // so that curr & prev months align on the same axis regardless of year
  const monthMap = {};
  ships.forEach(s => {
    if (!s.actualPickupDate) return;
    const d = moment(s.actualPickupDate);
    const key = MONTH_NAMES[d.month()]; // just "Jan", "Feb"…
    if (!monthMap[key]) monthMap[key] = { freight: 0, order: d.month() };
    monthMap[key].freight += s.quoteNetCharge || 0;
  });
  const sortedMonths = Object.entries(monthMap).sort(
    (a, b) => a[1].order - b[1].order
  );
  let cum = 0;
  const monthlyData = sortedMonths.map(([key, v]) => {
    cum += v.freight;
    return { month: key, freight: v.freight, cumFreight: cum };
  });

  // Freight by shipment type
  const modeMap = {};
  ships.forEach(s => {
    const mode = s.shipmentType || 'Other';
    modeMap[mode] = (modeMap[mode] || 0) + (s.quoteNetCharge || 0);
  });

  // Top 5 lanes (state→state)
  const laneMap = {};
  ships.forEach(s => {
    if (!s.originState || !s.destinationState) return;
    const key = `${s.originState} → ${s.destinationState}`;
    laneMap[key] = (laneMap[key] || 0) + (s.quoteNetCharge || 0);
  });
  const top5Lanes = Object.entries(laneMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([lane, spend]) => ({ lane, spend }));

  // Top 5 facilities (originCompany or originCity)
  const facMap = {};
  ships.forEach(s => {
    const fac = s.originCompany || s.originCity || 'Unknown';
    facMap[fac] = (facMap[fac] || 0) + (s.quoteNetCharge || 0);
  });
  const top5Fac = Object.entries(facMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, spend]) => ({ name, spend }));

  return {
    freight,
    count,
    weight,
    costPerLb,
    otdPct,
    monthlyData,
    modeMap,
    top5Lanes,
    top5Fac
  };
};

// ─── Main Component ───────────────────────────────────────────────────────────

const YOYFreightTab = ({
  allShipments = [], // shipsData (full unfiltered) from TMSDashboard
  filters = {} // current filter state from TMSDashboard
}) => {
  const [mode, setMode] = useState('yoy'); // default YOY
  const baseFilteredShipments = useMemo(() => {
    let data = allShipments;

    if (filters.customer !== 'All') {
      data = data.filter(s => s.destinationCompany === filters.customer);
    }
    if (filters.originState !== 'All') {
      data = data.filter(s => s.originState === filters.originState);
    }
    if (filters.destinationState !== 'All') {
      data = data.filter(s => s.destinationState === filters.destinationState);
    }
    if (filters.shipmentType !== 'All') {
      data = data.filter(s => s.shipmentType === filters.shipmentType);
    }
    if (filters.shipmentTypeRule !== 'All') {
      data = data.filter(
        s =>
          s.shipmentTypeRule?.trim().toLowerCase() ===
          filters.shipmentTypeRule.toLowerCase()
      );
    }
    if (filters.carrier !== 'All') {
      data = data.filter(s => s.carrierName === filters.carrier);
    }

    return data;
  }, [allShipments, filters]);
  // ── Derive date windows purely from filter dates + mode ──────────────────
  // No hardcoded years — everything flows from what's in the API data / filters
  const { currStart, currEnd, prevStart, prevEnd } = useMemo(() => {
    if (mode === 'ytd') {
      const latestDate = baseFilteredShipments.reduce((latest, s) => {
        if (!s.actualPickupDate) return latest;
        const d = moment(s.actualPickupDate);
        return d.isAfter(latest) ? d : latest;
      }, moment('2000-01-01'));

      const cs = latestDate.clone().startOf('year');
      const ce = latestDate.clone();
      const ps = cs.clone().subtract(1, 'year');
      const pe = ce.clone().subtract(1, 'year');
      return { currStart: cs, currEnd: ce, prevStart: ps, prevEnd: pe };
    }

    const cs = filters.startDate
      ? moment(filters.startDate)
      : moment().subtract(365, 'days');
    const ce = filters.endDate ? moment(filters.endDate) : moment();
    const ps = cs.clone().subtract(1, 'year');
    const pe = ce.clone().subtract(1, 'year');
    return { currStart: cs, currEnd: ce, prevStart: ps, prevEnd: pe };
  }, [mode, filters, baseFilteredShipments]);

  // ── Human-readable labels built from actual dates (no hardcoded years) ───
  const currLabel = buildRangeLabel(currStart, currEnd); // e.g. "Oct 28, 2024 – Jan 21, 2026"
  const prevLabel = buildRangeLabel(prevStart, prevEnd); // e.g. "Oct 28, 2023 – Jan 21, 2025"
  const currShortLabel = buildShortLabel(currStart, currEnd); // e.g. "2024–26"  (table column)
  const prevShortLabel = buildShortLabel(prevStart, prevEnd); // e.g. "2023–25"

  // ── Filter allShipments into the two windows ──────────────────────────────
  const currShipments = useMemo(
    () =>
      baseFilteredShipments.filter(s => {
        if (!s.actualPickupDate) return false;
        return moment(s.actualPickupDate).isBetween(
          currStart,
          currEnd,
          null,
          '[]'
        );
      }),
    [baseFilteredShipments, currStart, currEnd]
  );

  const prevShipments = useMemo(
    () =>
      baseFilteredShipments.filter(s => {
        if (!s.actualPickupDate) return false;
        return moment(s.actualPickupDate).isBetween(
          prevStart,
          prevEnd,
          null,
          '[]'
        );
      }),
    [baseFilteredShipments, prevStart, prevEnd]
  );

  // ── Compute metrics from actual filtered data ─────────────────────────────
  const curr = useMemo(() => computeMetrics(currShipments), [currShipments]);
  const prev = useMemo(() => computeMetrics(prevShipments), [prevShipments]);

  // ── Monthly grouped bar chart data ────────────────────────────────────────
  // Aligned by calendar month name (Jan, Feb…) — works across any year span
  const monthlyChartData = useMemo(() => {
    const currMap = Object.fromEntries(
      curr.monthlyData.map(m => [m.month, m.freight])
    );
    const prevMap = Object.fromEntries(
      prev.monthlyData.map(m => [m.month, m.freight])
    );
    const allMons = [
      ...new Set([...Object.keys(currMap), ...Object.keys(prevMap)])
    ].sort((a, b) => MONTH_NAMES.indexOf(a) - MONTH_NAMES.indexOf(b));
    return {
      categories: allMons,
      currData: allMons.map(m => Math.round(currMap[m] || 0)),
      prevData: allMons.map(m => Math.round(prevMap[m] || 0))
    };
  }, [curr, prev]);

  // ── Cumulative spend trend ────────────────────────────────────────────────
  const cumulativeData = useMemo(() => {
    const currMap = Object.fromEntries(
      curr.monthlyData.map(m => [m.month, m.cumFreight])
    );
    const prevMap = Object.fromEntries(
      prev.monthlyData.map(m => [m.month, m.cumFreight])
    );
    const allMons = [
      ...new Set([...Object.keys(currMap), ...Object.keys(prevMap)])
    ].sort((a, b) => MONTH_NAMES.indexOf(a) - MONTH_NAMES.indexOf(b));
    return {
      categories: allMons,
      currData: allMons.map(m => Math.round(currMap[m] || 0)),
      prevData: allMons.map(m => Math.round(prevMap[m] || 0))
    };
  }, [curr, prev]);

  // ── Mode (shipment type) comparison table ─────────────────────────────────
  const modeTableData = useMemo(() => {
    const allModes = [
      ...new Set([...Object.keys(curr.modeMap), ...Object.keys(prev.modeMap)])
    ];
    return allModes
      .map(m => ({
        mode: m,
        curr: curr.modeMap[m] || 0,
        prev: prev.modeMap[m] || 0
      }))
      .sort((a, b) => b.curr - a.curr);
  }, [curr, prev]);

  const modeDonutSeries = modeTableData.map(m => m.curr);
  const modeDonutLabels = modeTableData.map(m => m.mode);

  // ── Facility comparison chart ─────────────────────────────────────────────
  const facilityData = useMemo(() => {
    const allFacs = [
      ...new Set([
        ...curr.top5Fac.map(f => f.name),
        ...prev.top5Fac.map(f => f.name)
      ])
    ].slice(0, 5);
    const prevFacMap = Object.fromEntries(
      prev.top5Fac.map(f => [f.name, f.spend])
    );
    return {
      names: allFacs,
      currData: allFacs.map(
        n => curr.top5Fac.find(f => f.name === n)?.spend || 0
      ),
      prevData: allFacs.map(n => prevFacMap[n] || 0)
    };
  }, [curr, prev]);

  // ── Auto-generated insights ───────────────────────────────────────────────
  const insights = useMemo(() => {
    const list = [];
    const freightPct = parseFloat(pctDiff(curr.freight, prev.freight) || 0);
    const costLbPct = parseFloat(pctDiff(curr.costPerLb, prev.costPerLb) || 0);
    const otdDelta = parseFloat((curr.otdPct - prev.otdPct).toFixed(1));
    const countPct = parseFloat(pctDiff(curr.count, prev.count) || 0);
    const topMode = modeTableData[0];
    const topModePct = parseFloat(pctDiff(topMode?.curr, topMode?.prev) || 0);
    const modeLabel = mode.toUpperCase();

    if (prev.freight)
      list.push({
        color: freightPct < 0 ? '#2e7d32' : '#d32f2f',
        text: `Total freight spend is ${freightPct < 0 ? 'down' : 'up'} ${Math.abs(freightPct)}% ${modeLabel}, driven by ${freightPct < 0 ? 'lower' : 'higher'} rates.`
      });

    if (topMode && prev.modeMap[topMode.mode])
      list.push({
        color: '#1565C0',
        text: `${topMode.mode} spend ${topModePct > 0 ? 'increased' : 'decreased'} ${Math.abs(topModePct)}% ${modeLabel} due to volume changes.`
      });

    if (prev.costPerLb)
      list.push({
        color: costLbPct < 0 ? '#2e7d32' : '#d32f2f',
        text: `Cost per lb ${costLbPct < 0 ? 'improved' : 'worsened'} ${Math.abs(costLbPct)}% ${modeLabel} despite higher overall weight.`
      });

    if (prev.otdPct)
      list.push({
        color: otdDelta >= 0 ? '#2e7d32' : '#d32f2f',
        text: `On-time delivery ${otdDelta >= 0 ? 'improved' : 'declined'} ${Math.abs(otdDelta)} pts ${modeLabel}.`
      });

    if (prev.count)
      list.push({
        color: '#555',
        text: `Shipment count ${countPct > 0 ? 'up' : 'down'} ${Math.abs(countPct)}% ${modeLabel}.`
      });

    return list.slice(0, 5);
  }, [curr, prev, modeTableData, mode]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ backgroundColor: '#f5f7fa', pb: 1 }}>
      {/* ── Header + Toggle ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          px: 0.5
        }}
      >
        <Box>
          <Typography
            variant='h6'
            fontWeight='bold'
            fontSize='1rem'
            color='#1a237e'
          >
            {mode === 'yoy' ? 'Year-over-Year' : 'Year-to-Date'} Freight
            Dashboard
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            fontSize='0.7rem'
          >
            Shipper View — comparing {currLabel} vs {prevLabel}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Period chips — built purely from date math, no hardcoded years */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={currLabel}
              size='small'
              sx={{
                backgroundColor: '#1565C0',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.68rem'
              }}
            />
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={600}
              fontSize='0.7rem'
            >
              vs
            </Typography>
            <Chip
              label={prevLabel}
              size='small'
              sx={{
                backgroundColor: '#e0e0e0',
                color: '#333',
                fontWeight: 700,
                fontSize: '0.68rem'
              }}
            />
          </Box>

          {/* YOY / YTD Toggle */}
          <ToggleButtonGroup
            value={mode}
            exclusive
            size='small'
            onChange={(_, val) => {
              if (val) setMode(val);
            }}
            sx={{
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '10px',
              overflow: 'hidden',
              p: 0.3,
              gap: 0.3
            }}
          >
            <StyledToggle value='yoy' selectedcolor='#1565C0'>
              <ShowChart sx={{ fontSize: 16 }} />
              Year-over-Year
            </StyledToggle>
            <StyledToggle value='ytd' selectedcolor='#2e7d32'>
              <DateRange sx={{ fontSize: 16 }} />
              Year-to-Date
            </StyledToggle>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ── KPI Stat Cards ──────────────────────────────────────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {[
          {
            title: 'Total Freight Spend',
            icon: <AttachMoney sx={{ fontSize: 22, color: '#1565C0' }} />,
            currVal: fmtM(curr.freight, true),
            prevVal: fmtM(prev.freight, true),
            curr: curr.freight,
            prev: prev.freight,
            lowerIsBetter: true,
            color: '#1565C0'
          },
          {
            title: 'Total Shipments',
            icon: <LocalShipping sx={{ fontSize: 22, color: '#2e7d32' }} />,
            currVal: curr.count.toLocaleString(),
            prevVal: prev.count.toLocaleString(),
            curr: curr.count,
            prev: prev.count,
            lowerIsBetter: false,
            color: '#2e7d32'
          },
          {
            title: 'Total Weight (lbs)',
            icon: <Scale sx={{ fontSize: 22, color: '#e65100' }} />,
            currVal: fmtM(curr.weight),
            prevVal: fmtM(prev.weight),
            curr: curr.weight,
            prev: prev.weight,
            lowerIsBetter: false,
            color: '#e65100'
          },
          {
            title: 'Cost per lbs',
            icon: <AttachMoney sx={{ fontSize: 22, color: '#6a1b9a' }} />,
            currVal: `$${curr.costPerLb.toFixed(2)}`,
            prevVal: `$${prev.costPerLb.toFixed(2)}`,
            curr: curr.costPerLb,
            prev: prev.costPerLb,
            lowerIsBetter: true,
            color: '#6a1b9a'
          },
          {
            /* {
            title: 'On-Time Delivery',
            icon: <Schedule sx={{ fontSize: 22, color: '#00695c' }} />,
            currVal: `${curr.otdPct.toFixed(1)}%`,
            prevVal: `${prev.otdPct.toFixed(1)}%`,
            curr: curr.otdPct,
            prev: prev.otdPct,
            lowerIsBetter: false,
            color: '#00695c',
            isPts: true
          } */
          }
        ].map((card, i) => (
          <Grid item xs={12} sm={6} md key={i} sx={{ flex: '1 1 0' }}>
            <StatCard elevation={1} sx={{ borderTopColor: card.color }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}
              >
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                  fontSize='0.7rem'
                >
                  {card.title}
                </Typography>
                {card.icon}
              </Box>
              <Typography
                variant='h5'
                fontWeight='bold'
                sx={{ fontSize: '1.4rem', color: '#1a1a2e', lineHeight: 1.2 }}
              >
                {card.currVal}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexWrap: 'wrap'
                }}
              >
                <Delta
                  curr={card.curr}
                  prev={card.prev}
                  lowerIsBetter={card.lowerIsBetter}
                  isPts={card.isPts}
                />
                {/* <Typography
                  variant='caption'
                  color='text.disabled'
                  fontSize='0.65rem'
                >
                  vs {card.prevVal} prior period
                </Typography> */}
              </Box>
            </StatCard>
          </Grid>
        ))}
      </Grid>

      {/* ── Row 2: Monthly bar + Mode donut+table ───────────────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {/* Freight Spend grouped bar */}
        <Grid item xs={12} md={6}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Freight Spend {mode === 'yoy' ? 'YOY' : 'YTD'}
            </Typography>
            {/* Legend — labels come from actual date ranges */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}
            >
              {[
                { color: '#B0BEC5', label: `${prevShortLabel} (${prevLabel})` },
                { color: '#1565C0', label: `${currShortLabel} (${currLabel})` }
              ].map((leg, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 1,
                      backgroundColor: leg.color
                    }}
                  />
                  <Typography variant='caption' fontSize='0.68rem'>
                    {leg.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <ReactApexChart
              key={`bar-${mode}-${currShortLabel}`}
              options={{
                chart: { type: 'bar', height: 200, toolbar: { show: false } },
                plotOptions: {
                  bar: { columnWidth: '70%', grouped: true, borderRadius: 2 }
                },
                dataLabels: { enabled: false },
                xaxis: {
                  categories: monthlyChartData.categories,
                  labels: { style: { fontSize: '10px' } }
                },
                yaxis: {
                  labels: {
                    formatter: val =>
                      val >= 1_000_000
                        ? '$' + (val / 1_000_000).toFixed(1) + 'M'
                        : val >= 1_000
                          ? '$' + (val / 1_000).toFixed(0) + 'K'
                          : '$0',
                    style: { fontSize: '10px' }
                  }
                },
                colors: ['#B0BEC5', '#1565C0'],
                legend: { show: false },
                grid: { padding: { left: 5, right: 5, top: 0, bottom: 0 } },
                tooltip: {
                  shared: true,
                  intersect: false,
                  y: { formatter: val => '$' + val.toLocaleString() }
                }
              }}
              series={[
                { name: prevShortLabel, data: monthlyChartData.prevData },
                { name: currShortLabel, data: monthlyChartData.currData }
              ]}
              type='bar'
              height={200}
            />
          </SectionPaper>
        </Grid>

        {/* Freight Spend by Mode */}
        <Grid item xs={12} md={6}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Freight Spend by Mode {mode === 'yoy' ? 'YOY' : 'YTD'}
            </Typography>
            <Grid container spacing={1}>
              {/* Donut */}
              <Grid item xs={5}>
                <ReactApexChart
                  key={`donut-${mode}-${currShortLabel}`}
                  options={{
                    chart: { type: 'donut', height: 190 },
                    labels: modeDonutLabels,
                    colors: MODE_COLORS,
                    legend: { show: false },
                    dataLabels: { enabled: false },
                    plotOptions: {
                      pie: {
                        donut: {
                          size: '60%',
                          labels: {
                            show: true,
                            name: {
                              // ← add this
                              show: true,
                              fontSize: '10px',
                              fontWeight: 600
                            },
                            value: {
                              // ← THIS is what shows on hover — was missing
                              show: true,
                              fontSize: '13px',
                              fontWeight: 800,
                              formatter: val => fmtM(parseFloat(val), true) // ← formats to $1.2M
                            },
                            total: {
                              show: true,
                              label: `Total Spend`,
                              fontSize: '11px',
                              fontWeight: 700,
                              formatter: val => fmtM(curr.freight, true)
                            }
                          }
                        }
                      }
                    },
                    tooltip: {
                      y: { formatter: val => fmtM(val, true) }
                    }
                  }}
                  series={modeDonutSeries.length ? modeDonutSeries : [1]}
                  type='donut'
                  height={190}
                />
              </Grid>
              {/* Mode comparison table — column headers from dynamic short labels */}
              <Grid item xs={7}>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TH>Mode</TH>
                      <TH align='right'>{currShortLabel}</TH>
                      <TH align='right'>{prevShortLabel}</TH>
                      <TH align='right'>Δ %</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modeTableData.map((row, i) => {
                      const p = pctDiff(row.curr, row.prev);
                      const isUp = p !== null && parseFloat(p) > 0;
                      return (
                        <TableRow
                          key={i}
                          sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                        >
                          <TD>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: MODE_COLORS[i] || '#ccc',
                                  flexShrink: 0
                                }}
                              />
                              {row.mode}
                            </Box>
                          </TD>
                          <TD align='right' sx={{ fontWeight: 600 }}>
                            {fmtM(row.curr, true)}
                          </TD>
                          <TD align='right' sx={{ color: '#888' }}>
                            {fmtM(row.prev, true)}
                          </TD>
                          <TD align='right'>
                            {p !== null ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.2,
                                  color: isUp ? '#d32f2f' : '#2e7d32',
                                  fontWeight: 700,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {isUp ? (
                                  <TrendingDown sx={{ fontSize: 11 }} />
                                ) : (
                                  <TrendingUp sx={{ fontSize: 11 }} />
                                )}
                                {Math.abs(parseFloat(p)).toFixed(1)}%
                              </Box>
                            ) : (
                              <Typography
                                variant='caption'
                                color='text.disabled'
                              >
                                —
                              </Typography>
                            )}
                          </TD>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Grid>
            </Grid>
          </SectionPaper>
        </Grid>
      </Grid>

      {/* ── Row 3: Top 5 Lanes + Facility + Cumulative Trend ────────────── */}
      <Grid container spacing={0.75} sx={{ mb: 1 }}>
        {/* Top 5 Lanes table */}
        <Grid item xs={12} md={3}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={1}
            >
              Top 5 Lanes by Spend {mode === 'yoy' ? 'YOY' : 'YTD'}
            </Typography>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TH>Lane</TH>
                  <TH align='right'>{currShortLabel}</TH>
                  <TH align='right'>{prevShortLabel}</TH>
                  <TH align='right'>Δ %</TH>
                </TableRow>
              </TableHead>
              <TableBody>
                {curr.top5Lanes.length === 0 ? (
                  <TableRow>
                    <TD
                      colSpan={4}
                      align='center'
                      sx={{ color: '#bbb', py: 2 }}
                    >
                      No data
                    </TD>
                  </TableRow>
                ) : (
                  (() => {
                    const prevLaneMap = Object.fromEntries(
                      prev.top5Lanes.map(l => [l.lane, l.spend])
                    );
                    return curr.top5Lanes.map((row, i) => {
                      const prevSpend = prevLaneMap[row.lane] || 0;
                      const p = pctDiff(row.spend, prevSpend);
                      const isUp = p !== null && parseFloat(p) > 0;
                      return (
                        <TableRow
                          key={i}
                          sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                        >
                          <TD sx={{ fontWeight: 600, color: '#1a237e' }}>
                            {row.lane}
                          </TD>
                          <TD align='right' sx={{ fontWeight: 600 }}>
                            {fmtM(row.spend, true)}
                          </TD>
                          <TD align='right' sx={{ color: '#888' }}>
                            {fmtM(prevSpend, true)}
                          </TD>
                          <TD align='right'>
                            {p !== null ? (
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.2,
                                  color: isUp ? '#d32f2f' : '#2e7d32',
                                  fontWeight: 700,
                                  fontSize: '0.7rem'
                                }}
                              >
                                {isUp ? (
                                  <TrendingDown sx={{ fontSize: 11 }} />
                                ) : (
                                  <TrendingUp sx={{ fontSize: 11 }} />
                                )}
                                {Math.abs(parseFloat(p)).toFixed(1)}%
                              </Box>
                            ) : (
                              '—'
                            )}
                          </TD>
                        </TableRow>
                      );
                    });
                  })()
                )}
              </TableBody>
            </Table>
          </SectionPaper>
        </Grid>

        {/* Spend by Facility */}
        <Grid item xs={12} md={5}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Spend by Ship From Facility {mode === 'yoy' ? 'YOY' : 'YTD'}
            </Typography>
            {/* <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}
            >
              {[
                { color: '#1565C0', label: `${currShortLabel} Spend` },
                { color: '#B0BEC5', label: `${prevShortLabel} Spend` }
              ].map((leg, i) => (
                <Box
                  key={i}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 8,
                      backgroundColor: leg.color,
                      borderRadius: 1
                    }}
                  />
                  <Typography variant='caption' fontSize='0.68rem'>
                    {leg.label}
                  </Typography>
                </Box>
              ))}
            </Box> */}
            {/* <ReactApexChart
              key={`facility-${mode}-${currShortLabel}`}
              options={{
                chart: { type: 'bar', height: 200, toolbar: { show: false } },
                plotOptions: {
                  bar: {
                    horizontal: true,
                    barHeight: '70%',
                    grouped: true,
                    borderRadius: 2
                  }
                },
                dataLabels: { enabled: false },
                xaxis: {
                  labels: {
                    formatter: val =>
                      val >= 1_000_000
                        ? '$' + (val / 1_000_000).toFixed(1) + 'M'
                        : val >= 1_000
                          ? '$' + (val / 1_000).toFixed(0) + 'K'
                          : '$0',
                    style: { fontSize: '10px' }
                  }
                },
                yaxis: {
                  categories: facilityData.names,
                  labels: {
                    show: true,
                    style: { fontSize: '10px', fontWeight: 600 },
                    maxWidth: 120,
                    formatter: val =>
                      val?.length > 14 ? val.slice(0, 14) + '…' : val
                  }
                },
                colors: ['#1565C0', '#B0BEC5'],
                legend: { show: false },
                grid: { padding: { left: 5, right: 10 } },
                tooltip: {
                  shared: true,
                  intersect: false,
                  y: { formatter: val => '$' + val.toLocaleString() }
                }
              }}
              series={[
                {
                  name: `${currShortLabel} Spend`,
                  data: facilityData.currData
                },
                { name: `${prevShortLabel} Spend`, data: facilityData.prevData }
              ]}
              type='bar'
              height={200}
            /> */}
          </SectionPaper>
        </Grid>

        {/* Cumulative Spend Trend */}
        <Grid item xs={12} md={4}>
          <SectionPaper elevation={1}>
            <Typography
              variant='subtitle2'
              fontWeight='bold'
              fontSize='0.85rem'
              mb={0.5}
            >
              Spend Trend {mode === 'yoy' ? 'YOY' : 'YTD'} (Cumulative)
            </Typography>
            <ReactApexChart
              key={`cumulative-${mode}-${currShortLabel}`}
              options={{
                chart: { type: 'line', height: 215, toolbar: { show: false } },
                stroke: { width: 2, curve: 'smooth', dashArray: [5, 0] },
                markers: { size: 4 },
                xaxis: {
                  categories: cumulativeData.categories,
                  labels: { style: { fontSize: '10px' } }
                },
                yaxis: {
                  labels: {
                    formatter: val =>
                      val >= 1_000_000
                        ? '$' + (val / 1_000_000).toFixed(0) + 'M'
                        : val >= 1_000
                          ? '$' + (val / 1_000).toFixed(0) + 'K'
                          : '$0',
                    style: { fontSize: '10px' }
                  }
                },
                colors: ['#B0BEC5', '#1565C0'],
                legend: { show: true, position: 'top', fontSize: '10px' },
                grid: { padding: { left: 5, right: 5 } },
                tooltip: {
                  shared: true,
                  intersect: false,
                  y: { formatter: val => '$' + val.toLocaleString() }
                }
              }}
              series={[
                { name: prevShortLabel, data: cumulativeData.prevData },
                { name: currShortLabel, data: cumulativeData.currData }
              ]}
              type='line'
              height={215}
            />
          </SectionPaper>
        </Grid>
      </Grid>

      {/* ── Insights Bar ────────────────────────────────────────────────── */}
      {/* <Paper
        elevation={1}
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          backgroundColor: '#fffde7',
          borderLeft: '4px solid #f9a825'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0
          }}
        >
          <EmojiObjects sx={{ fontSize: 18, color: '#f9a825' }} />
          <Typography
            variant='caption'
            fontWeight='bold'
            fontSize='0.72rem'
            color='#795548'
          >
            Insights
          </Typography>
        </Box>
        <Divider orientation='vertical' flexItem />
        {insights.length === 0 ? (
          <Typography variant='caption' color='text.disabled'>
            No data available for insights.
          </Typography>
        ) : (
          insights.map((ins, i) => (
            <Box
              key={i}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: ins.color,
                  flexShrink: 0
                }}
              />
              <Typography
                variant='caption'
                sx={{ fontSize: '0.68rem', color: '#555' }}
              >
                {ins.text}
              </Typography>
              {i < insights.length - 1 && (
                <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />
              )}
            </Box>
          ))
        )}
      </Paper> */}
    </Box>
  );
};

export default YOYFreightTab;
